import PropTypes from 'prop-types';
import React, { Component, Suspense } from 'react';
import Actions from '../../actions/actions';

import { connect } from 'react-redux';
import EmptyStep from './empty_step';
import {
  customerLoader,
  energyDetailsLoader,
  financingMethodLoader,
  financingRequestLoader,
  proposalLoader,
} from '../../actions/data_loaders';
import PageSpinner from '../shared/page_spinner';
import withDataLoaded from '../with_data_loaded';
import StoreHelper from '../../helpers/store_helper';
import cx from 'classnames';

import { WizardSteps } from './wizard_steps';
import { ReactComponent as Sun } from '../../images/sun.svg';
import Log from '../../helpers/log';
import SleepHelper from '../../helpers/sleep_helper';
import withFormState from '../forms/with_form_state';
import { applicantEligibility, coApplicantEligibility } from '../customer/config/form_fields';
import Config from '../../config/config';
import SuperAdminMenu from './super_admin_menu';
import ProductNotAvailable from '../shared/product_not_available';
import ProposalHelper from '../../helpers/proposal_helper';
import WizardV2 from './wizardv2';

let { pollingEnabled } = Config;

class ProposalWizard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeStep: null,
      newStep: null,
    };
    this.WizardSteps = new WizardSteps(props);
  }

  componentDidMount() {
    let lastComplete = this.WizardSteps.getLastStep().isComplete(this.props);

    if (lastComplete) {
      this.confirmationPageRedirect();
    } else {
      this.eagerLoadFinancingMethod(this.props);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.page !== this.props.page && !this.isProcessing()) {
      this.page = this.props.page;
      this.changePage(this.props.page);
    } else if (this.state.newStep !== null && this.state.activeStep !== this.state.newStep && !this.isProcessing()) {
      this.changeStepNum(this.state.activeStep, this.state.newStep);
    }

    const currentFinancingMethodId = prevProps.financingMethodId;
    if (currentFinancingMethodId) {
      if (currentFinancingMethodId && currentFinancingMethodId !== this.props.financingMethod.id) {
        this.eagerLoadFinancingMethod(this.props);
      }
    }
  }

  confirmationPageRedirect() {
    Actions.setRoute('/confirmation');
    Actions.setView({ page: 'confirmation' });
  }

  async changeStepNum(oldStepNum, newStepNum) {
    let lastComplete = this.WizardSteps.getLastStep().isComplete(this.props);

    if (lastComplete) {
      this.confirmationPageRedirect();
      return;
    }

    if (this.WizardSteps.getStep(oldStepNum).isProcessingLocksStep() && this.isProcessing()) {
      return;
    }

    let nextStep = this.WizardSteps.getStep(newStepNum);

    if (!nextStep.isClickable(oldStepNum, this.props)) {
      return;
    }

    try {
      await this.WizardSteps.getStep(oldStepNum).onLeave(newStepNum, this.props);

      if (this.isProcessing()) {
        try {
          await SleepHelper.wait(() => !this.isProcessing(), 5000, 250);
        } catch (e) {}
      }

      if (newStepNum > oldStepNum && !this.WizardSteps.getStep(oldStepNum).isComplete(this.props)) {
        return;
      }

      if (!this.WizardSteps.getStep(newStepNum).isNavigable(oldStepNum, this.props)) {
        return;
      }

      window.scrollTo(0, 0);

      if (this.state.activeStep === newStepNum) {
        this.setState({ newStep: null });
        return;
      }

      if (this.isProcessing()) {
        try {
          await SleepHelper.wait(() => !this.isProcessing(), 5000, 250);
        } catch (e) {
          Log.debug(`this.isProcessing()`);
          this.setState({ newStep: newStepNum });
          return;
        }
      }

      // for when you sorta skip a step via finishing the step after,
      // go back and then go forward 2, because your an evil QA engineer :-p
      await this.WizardSteps.stepsBeforeRunAfter(newStepNum, this.props);

      if (newStepNum > oldStepNum && !(await this.WizardSteps.getStep(oldStepNum).isComplete(this.props))) {
        return;
      }

      try {
        await nextStep.onLoad(oldStepNum, this.props);
      } catch (onLoadException) {
        Log.debug(
          `an exception occurred during wizard change step onLoad ${onLoadException.message} \n ${onLoadException.stack}`,
          onLoadException
        );
        // throw new Error(`an exception occurred during wizard change step onLoad ${onLoadException.message} \n ${onLoadException.stack}`, onLoadException);
      }
    } catch (onLeaveException) {
      Log.debug(
        `an exception occurred during wizard change step onLeave ${
          onLeaveException ? onLeaveException.message : ''
        } \n ${onLeaveException ? onLeaveException.stack : ''}`,
        onLeaveException
      );
      // throw new Error(`an exception occurred during wizard change step onLeave ${onLeaveException.message} \n ${onLeaveException.stack}`, onLeaveException);
    }
  }

  changePage(page) {
    const index = this.WizardSteps.stepIndexById(page);
    if (index === -1) {
      return;
    }
    this.setState({
      activeStep: index,
      newStep: null,
    });
  }

  isProcessing = () => {
    const {
      locks: {
        updateFinancingMethod,
        updateCustomer,
        irDocGeneration,
        uploadFile,
        fetchFinancingRequest,
        smssignaturelock,
      },
    } = this.props;
    return (
      !!updateFinancingMethod ||
      !!updateCustomer ||
      !!irDocGeneration ||
      !!uploadFile ||
      !!fetchFinancingRequest ||
      !!smssignaturelock
    );
  };

  calculateClassNames(stepIndex) {
    const currentStepConfig = this.WizardSteps.getStep(stepIndex);
    const previousStepConfig = stepIndex === 0 ? null : this.WizardSteps.getStep(stepIndex - 1);

    let additionalClassName = [];

    additionalClassName.push(`${currentStepConfig.pageId}-wizard-header`);

    if (
      !currentStepConfig.isClickable(this.state.activeStep, this.props) ||
      (previousStepConfig && !previousStepConfig.stepsNavigation(this.props))
    ) {
      additionalClassName.push('disabled');
    }

    if (currentStepConfig.isNavigable(this.state.activeStep, this.props)) {
      additionalClassName.push('navigable');
    }

    if (stepIndex === this.state.activeStep) {
      additionalClassName.push('isActiveStep');
    }

    return additionalClassName.join(' ');
  }

  eagerLoadFinancingMethod(props) {
    const {
      proposal: { id: proposalId },
      customer,
    } = props;
    let financingMethodId;
    const simpleFinancingMethod = StoreHelper.getFinancingMethodToFetch(props);
    financingMethodId = simpleFinancingMethod.id;

    Actions.getFinancingMethod({ proposalId, id: financingMethodId });

    if (pollingEnabled) {
      Actions.beginProposalPagePolling({ customerId: customer.id, proposalId });
    }
  }

  isCustomizeProposalPage(page) {
    return page === 'savingsCalculator';
  }

  render() {
    const { children } = this.props;
    const { activeStep } = this.state;

    if (!ProposalHelper.isFinancingMethodValid({ financingMethod: this?.props?.financingMethod })) {
      return (
        <div>
          <ProductNotAvailable />
        </div>
      );
    }

    const wizardSteps = this.WizardSteps.getSteps().map((name, index) => ({
      component: <EmptyStep />,
      additionalClassName: this.calculateClassNames(index),
      icon: this.WizardSteps.getStep(index).icon,
      label: this.WizardSteps.getStep(index).label,
    }));

    let hasBreadcrumb,
      stepsNavigation = true;

    let nextButtonCls = '';

    if (activeStep !== null) {
      wizardSteps[activeStep].component = (
        <div className="wizard-step-wrapper">
          <Suspense fallback={<PageSpinner />}>{children}</Suspense>
        </div>
      );
      if (typeof this.WizardSteps.getStep(activeStep).hasBreadcrumb === 'function') {
        hasBreadcrumb = this.WizardSteps.getStep(activeStep).hasBreadcrumb(this.props);
        Actions.setReduxVar({ key: 'hideHeader', value: !hasBreadcrumb });
      }
      if (typeof this.WizardSteps.getStep(activeStep).stepsNavigation === 'function') {
        stepsNavigation = this.WizardSteps.getStep(activeStep).stepsNavigation(this.props);
      }
      if (typeof this.WizardSteps.getStep(activeStep).nextButtonCls === 'function') {
        nextButtonCls = this.WizardSteps.getStep(activeStep).nextButtonCls(this.props);
      }
    }

    let proposalPageFooterButtonClass = this.isCustomizeProposalPage(this.props.page)
      ? 'proposal-page-footer-button'
      : '';

    return (
      <>
        {this.props.user?.atype === 'CPF' && <SuperAdminMenu />}
        <div className="step-progress" hidden={activeStep === null}>
          <WizardV2
            showSteps={hasBreadcrumb}
            stepsNavigation={stepsNavigation}
            steps={wizardSteps}
            currentStep={activeStep}
            dontAllowStepMoves={true}
            preventEnterSubmission={activeStep === 0}
            proposal={this.props.proposal}
            customer={this.props.customer}
            onBeforeStepChange={(prevStep, nextStep) => {
              window.scrollTo(0, 0);
              return this.changeStepNum(prevStep, nextStep);
            }}
            backButtonCls={cx('btn btn-prev btn-primary btn-lg pull-left action-secondary prev-button', {
              processing: this.isProcessing(),
            })}
            backButtonChildren={
              <div>
                <div className="action-contents">Back</div>
                {this.isProcessing() && (
                  <div className="processing-icon">
                    <Sun name="sun" className="sun-icon icon" />
                  </div>
                )}
              </div>
            }
            backButtonText=""
            nextButtonCls={cx(
              'btn btn-next btn-primary btn-lg pull-right action-primary next-button',
              proposalPageFooterButtonClass,
              nextButtonCls,
              { processing: this.isProcessing() }
            )}
            nextButtonChildren={
              <div>
                <div className="action-contents">Continue</div>
                {this.isProcessing() && (
                  <div className="processing-icon">
                    <Sun name="sun" className="sun-icon icon" />
                  </div>
                )}
              </div>
            }
            nextButtonText=""
          />
        </div>
      </>
    );
  }
}

ProposalWizard.propTypes = {
  page: PropTypes.string.isRequired,
  financingMethod: PropTypes.object,
  financingRequest: PropTypes.object,
  proposal: PropTypes.object,
  locks: PropTypes.object.isRequired,
  proposalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  financingMethodId: PropTypes.number,
  customer: PropTypes.object,
  user: PropTypes.object,
  isGuestUser: PropTypes.bool.isRequired,
  billBeforeAgree: PropTypes.bool,
  defaultServiceAddressType: PropTypes.string,
  customerForm: PropTypes.object,
  customerFormValid: PropTypes.bool,
  userFunctionalityAcls: PropTypes.array,
  updateFinancingMethodLocked: PropTypes.number,
};

const EligibilityAndCustomerForm = withFormState(
  {
    formName: 'eligibilityForm',
    getInitialState(props) {
      const { csecondaryfirstname, csecondarylastname, csecondaryphone, csecondaryemail } = props.customer;

      return {
        [applicantEligibility.annualIncome]: 0,
        [coApplicantEligibility.annualIncome]: 0,
        csecondaryfirstname,
        csecondarylastname,
        csecondaryphone,
        csecondaryemail,
      };
    },
  },
  withFormState(
    {
      formName: 'customerForm',
      getInitialState(props) {
        return { ...props.customer };
      },
    },
    ProposalWizard
  )
);

const ConnectedProposalWizard = connect(
  ({
    financingMethod,
    proposal,
    locks,
    financingRequest,
    customer,
    user,
    isGuestUser,
    billBeforeAgree,
    defaultServiceAddressType,
    eligibility,
    customerFormValid,
    userFunctionalityAcls,
    locks: { updateFinancingMethod: updateFinancingMethodLocked },
    userInputLoanAmountIsInvalid,
  }) => {
    return {
      financingMethod,
      proposal,
      locks,
      financingRequest,
      customer,
      user,
      isGuestUser,
      billBeforeAgree,
      defaultServiceAddressType,
      eligibility,
      customerFormValid,
      userFunctionalityAcls,
      updateFinancingMethodLocked,
      userInputLoanAmountIsInvalid,
    };
  }
)(EligibilityAndCustomerForm);

const WithCustomerAndFinancingMethodAndFinancingRequestAndEnergyDetails = withDataLoaded({
  WithData: ConnectedProposalWizard,
  WithoutData: PageSpinner,
  data: [customerLoader, energyDetailsLoader, financingMethodLoader, financingRequestLoader],
});

const WithProposal = withDataLoaded({
  WithData: WithCustomerAndFinancingMethodAndFinancingRequestAndEnergyDetails,
  WithoutData: PageSpinner,
  data: [proposalLoader],
});

export default WithProposal;
